import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../provider/snackbar";
import { InstalledBaseNodeReportFile } from "../graphql/queries/installedBaseNodeReportFile";
import { downloadFile } from "../utils/downloadFile";
import { InstalledBaseFileTarget } from "../utils/novuNotificationPath";

/**
 * Downloads an IoT report file referenced by a notification target
 * (/installedBase/{nodeId}/files/{fileId}) with progress/success/error
 * feedback via the global snackbar. Used by the Novu inbox interception
 * (relative redirect paths) and the fallback deep-link route (absolute
 * redirect URLs open in a new tab).
 */
export const useReportFileDownload = () => {
    const { t } = useTranslation();
    const apolloClient = useApolloClient();
    const { startLocalProgress, finishLocalProgress, errorLocalProgress } = useSnackbar();

    const downloadReportFile = async ({ nodeId, fileId }: InstalledBaseFileTarget) => {
        startLocalProgress(t("Downloading report…"));
        try {
            const res = await apolloClient.query({
                query: InstalledBaseNodeReportFile,
                variables: { id: nodeId },
                // The file link is presigned; always fetch a fresh one.
                fetchPolicy: "network-only",
            });
            const files = res.data?.installedBaseNode?.fileList?.installedBaseNodeFiles ?? [];
            const entry = files.find((f: any) => String(f?.id) === fileId);
            if (!entry?.file?.link) {
                errorLocalProgress(t("Report not found or no access"));
                return;
            }
            await downloadFile(entry.file.link, entry.file.basename || entry.filename || "report");
            finishLocalProgress(t("Report downloaded."));
        } catch {
            errorLocalProgress(t("Report not found or no access"));
        }
    };

    return { downloadReportFile };
};
